import { calendarTerminService } from "../../../services/calendarTerminService";
import { documentService } from "../../../services/documentService";
import { mailLinkService } from "../../../services/mailLinkService";
import { telephonyService } from "../../../services/telephonyService";
import { vorgangService } from "../../../services/vorgangService";
import type { VerlaufEintrag } from "../v3/verlauf";
import { zurueckAnVorgangPatch } from "./ereignisAktionen";

/**
 * Persistiert das Ziel des V4-Rechtsklick-Menüs für jede Ereignisart.
 *
 * Der Termin schreibt in den Sidecar-Slot. Bei historischem Bestand, der nur
 * über ein To-do im Vorgang auftauchte, wird vor JEDEM To-do-Ziel der Vorgang
 * gesetzt: `setTodo` akzeptiert nur Ziele aus dem Vorgang des Sidecars. Beim
 * Zurückhängen an den Vorgang verhindert das außerdem, dass der Termin nach
 * `setTodo(null)` ganz aus der Akte verschwindet.
 */
export async function speichereEreignisZuweisung(
    eintrag: VerlaufEintrag,
    todoId: number | null,
    vorgangId: number,
    /**
     * Bei einer Mail-Karte ALLE Links, die sie vertritt (T-11: dieselbe Mail
     * aus mehreren Postfächern). Ohne Angabe nur der Link der Karte.
     */
    mailLinkIds?: readonly number[],
): Promise<void> {
    if (eintrag.mail) {
        const ids = mailLinkIds && mailLinkIds.length > 0 ? mailLinkIds : [eintrag.mail.id];
        for (const lid of ids) await mailLinkService.setTodo(lid, todoId);
    } else if (eintrag.dokument) {
        // Ein Gast-Dokument ist anderswo zu Hause: sein `todoId` gehört dem
        // fremden Vorgang. Verschoben wird hier nur die VERKNÜPFUNG — ein
        // `PUT /documents/:id { todoId }` risse es aus seiner Heimat.
        if (eintrag.dokument.gast) {
            await vorgangService.verknuepfeDokumente(vorgangId, [eintrag.dokument.id], todoId);
        } else {
            await documentService.update(
                eintrag.dokument.id,
                todoId == null ? zurueckAnVorgangPatch(vorgangId) : { todoId },
            );
        }
    } else if (eintrag.anruf) {
        // PO 01.10.2026: Ein Anruf kann an mehreren To-dos hängen — umgehängt
        // wird genau DIE Zuordnung, die diese Karte trägt (auch bei der
        // Übernahme ins erste To-do). Die übrigen bleiben, wo sie sind.
        if (eintrag.zuordnungId != null) {
            await telephonyService.moveZuordnung(eintrag.anruf.id, eintrag.zuordnungId, todoId);
        } else {
            // Altbestand ohne Zuordnungs-Id (ältere Antwort): der veraltete
            // PATCH, den der Server als Adapter weiter versteht.
            await telephonyService.updateCall(
                eintrag.anruf.id,
                todoId == null ? zurueckAnVorgangPatch(vorgangId) : { vorgangId, todoId },
            );
        }
    } else if (eintrag.termin) {
        const termin = eintrag.termin;
        if (termin.vorgangId == null) {
            await calendarTerminService.setVorgang(
                termin.mailbox,
                termin.graphEventId,
                vorgangId,
            );
        }
        await calendarTerminService.setTodo(termin.mailbox, termin.graphEventId, todoId);
    }
}
